import { useEffect, useRef, useState } from 'react';
import {
  ADJUNTO_MAX_BYTES,
  ADJUNTO_MIMES,
  type AdjuntoMensaje,
  type AlcanceMensaje,
  type MensajeGuardado,
  type MensajeResumen,
  type Pagina,
} from '@kodica/shared';

import { Iconos } from '../componentes/iconos';
import {
  Boton,
  Buscador,
  Campo,
  Cargando,
  Chip,
  Encabezado,
  Entrada,
  ErrorCaja,
  Modal,
  Panel,
  Seleccion,
  Vacio,
} from '../componentes/ui';
import { ErrorApi, pedir, subirArchivo } from '../lib/api';
import { cuando } from '../lib/fechas';
import { conLigas } from '../lib/ligas';
import { consulta, useRecurso, useRetardo } from '../lib/consultas';

/**
 * Avisos de la administración a los residentes.
 *
 * ## Por qué hay borrador y publicación, y no un solo botón
 *
 * Porque **publicar no se deshace**. En cuanto sale, está en el teléfono de
 * doscientas personas; no hay forma de recogerlo. Un aviso mal escrito, con la
 * fecha equivocada o mandado a quien no era, es un problema que ya no se
 * arregla desde aquí.
 *
 * Así que se escribe, se adjunta, se elige a quién va — y todo eso se puede
 * corregir cuantas veces haga falta. Solo el último paso es de ida.
 */

interface Unidad {
  id: string;
  clave: string;
}

const ALCANCES: Array<{ valor: AlcanceMensaje; texto: string; ayuda: string }> = [
  {
    valor: 'TODOS',
    texto: 'Todos los habitantes',
    ayuda: 'Todo el que viva ahí y esté dado de alta',
  },
  { valor: 'TITULARES', texto: 'Solo titulares', ayuda: 'Quien responde por la vivienda' },
];

export function AvisosPagina() {
  const avisos = useRecurso<MensajeResumen[]>('/mensajes');
  /** `null` = cerrado · `'nuevo'` = uno en blanco · un id = corrigiendo ese. */
  const [redactando, setRedactando] = useState<string | null>(null);

  return (
    <>
      <Encabezado titulo="Avisos" descripcion="Lo que la administración le manda a los residentes">
        <Boton variante="primario" icono={Iconos.agregar} onClick={() => setRedactando('nuevo')}>
          Nuevo aviso
        </Boton>
      </Encabezado>

      {avisos.error && <ErrorCaja>{avisos.error}</ErrorCaja>}
      {avisos.cargando && <Cargando />}

      {avisos.datos?.length === 0 && (
        <Vacio>
          Todavía no hay avisos. Desde aquí se le manda uno a una vivienda, a varias o a todo el
          fraccionamiento.
        </Vacio>
      )}

      <div className="flex flex-col gap-3">
        {avisos.datos?.map((m) => (
          <TarjetaAviso
            key={m.id}
            aviso={m}
            alCambiar={avisos.recargar}
            alEditar={() => setRedactando(m.id)}
          />
        ))}
      </div>

      {redactando && (
        <Redactor
          // Sin `key`, React reutilizaría el mismo formulario al pasar de uno a
          // otro y se quedarían dentro los datos del anterior.
          key={redactando}
          idExistente={redactando === 'nuevo' ? null : redactando}
          alCerrar={() => setRedactando(null)}
          alGuardar={() => {
            setRedactando(null);
            avisos.recargar();
          }}
        />
      )}
    </>
  );
}

function TarjetaAviso({
  aviso,
  alCambiar,
  alEditar,
}: {
  aviso: MensajeResumen;
  alCambiar: () => void;
  alEditar: () => void;
}) {
  const [ocupado, setOcupado] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirmando, setConfirmando] = useState(false);

  const borrador = aviso.publicadoEn === null;

  async function publicar() {
    setOcupado(true);
    setError(null);
    try {
      await pedir(`/mensajes/${aviso.id}/publicar`, { metodo: 'POST' });
      setConfirmando(false);
      alCambiar();
    } catch (e) {
      setError(e instanceof ErrorApi ? e.message : 'No se pudo publicar');
    } finally {
      setOcupado(false);
    }
  }

  async function borrar() {
    setOcupado(true);
    try {
      await pedir(`/mensajes/${aviso.id}`, { metodo: 'DELETE' });
      alCambiar();
    } catch (e) {
      setError(e instanceof ErrorApi ? e.message : 'No se pudo borrar');
      setOcupado(false);
    }
  }

  return (
    // `px-6 py-5` dentro del `Panel`, como el resto del portal: `Panel` no trae
    // relleno propio, y sin esto el texto queda pegado al borde.
    <Panel>
      <div className="px-6 py-5">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <h3 className="min-w-0 text-headline-md text-on-surface">{aviso.titulo}</h3>

          {borrador ? (
            <Chip tono="aviso">Borrador</Chip>
          ) : (
            // Cuántos lo abrieron, no solo cuántos lo recibieron: es la única
            // señal de si el aviso sirvió de algo.
            <Chip tono="ok">
              {aviso.leidos} de {aviso.entregados} leído{aviso.leidos === 1 ? '' : 's'}
            </Chip>
          )}
        </div>

        {/* El cuerpo debajo y a todo el ancho. Antes compartía renglón con la
            etiqueta de la derecha, así que se estrechaba a media columna y
            quedaba desalineado con el título. */}
        <p className="mt-2 whitespace-pre-wrap text-body-md text-on-surface-variant">
          {conLigas(aviso.cuerpo)}
        </p>

        <div className="mt-4 flex flex-wrap items-center gap-2 text-label-sm text-on-surface-variant">
          <Chip>{aviso.paraTodas ? 'Todas las viviendas' : `${aviso.unidades} vivienda(s)`}</Chip>
          <Chip>{aviso.alcance === 'TITULARES' ? 'Solo titulares' : 'Todos los habitantes'}</Chip>
          {aviso.adjuntos.length > 0 && (
            <Chip>
              {aviso.adjuntos.length} adjunto{aviso.adjuntos.length === 1 ? '' : 's'}
            </Chip>
          )}
          <span>
            {aviso.autor ? `${aviso.autor} · ` : ''}
            {cuando(aviso.publicadoEn ?? aviso.creadoEn)}
          </span>
        </div>

        {error && (
          <div className="mt-3">
            <ErrorCaja>{error}</ErrorCaja>
          </div>
        )}

        {borrador && (
          <div className="mt-4 flex gap-2">
            <Boton variante="primario" disabled={ocupado} onClick={() => setConfirmando(true)}>
              Publicar
            </Boton>
            <Boton variante="contorno" disabled={ocupado} onClick={alEditar}>
              Corregir
            </Boton>
            <Boton variante="texto" disabled={ocupado} onClick={borrar}>
              Borrar
            </Boton>
          </div>
        )}
      </div>

      {/* **Se confirma porque no se deshace.** Es lo único de esta pantalla que
          no admite corrección: en cuanto sale, está en el teléfono de la gente. */}
      {confirmando && (
        <Modal titulo="Publicar el aviso" abierto alCerrar={() => setConfirmando(false)}>
          <p className="text-body-md text-on-surface-variant">
            Va a salir a {aviso.paraTodas ? 'todas las viviendas' : `${aviso.unidades} vivienda(s)`}
            , para {aviso.alcance === 'TITULARES' ? 'los titulares' : 'todos los habitantes'}.
          </p>
          <p className="mt-2 text-body-md text-on-surface-variant">
            <strong>Una vez publicado no se puede corregir ni retirar</strong>, porque ya estará en
            los teléfonos de quien lo reciba.
          </p>
          <div className="mt-4 flex justify-end gap-2">
            <Boton variante="texto" onClick={() => setConfirmando(false)}>
              Cancelar
            </Boton>
            <Boton variante="primario" disabled={ocupado} onClick={publicar}>
              {ocupado ? 'Publicando…' : 'Publicar'}
            </Boton>
          </div>
        </Modal>
      )}
    </Panel>
  );
}

/**
 * Redactar un aviso.
 *
 * Se crea como borrador y **después** se le cuelgan los adjuntos: el archivo
 * necesita un aviso al que pertenecer, y guardarlo en memoria hasta el final
 * significaría perderlo si la subida falla.
 */
function Redactor({
  idExistente,
  alCerrar,
  alGuardar,
}: {
  /** `null` para uno nuevo; el id del borrador que se está corrigiendo. */
  idExistente: string | null;
  alCerrar: () => void;
  alGuardar: () => void;
}) {
  const [titulo, setTitulo] = useState('');
  const [cuerpo, setCuerpo] = useState('');
  const [alcance, setAlcance] = useState<AlcanceMensaje>('TODOS');
  const [paraTodas, setParaTodas] = useState(true);
  const [elegidas, setElegidas] = useState<Unidad[]>([]);
  const [archivos, setArchivos] = useState<File[]>([]);
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [arrastrando, setArrastrando] = useState(false);
  const entradaArchivos = useRef<HTMLInputElement>(null);

  /** Los adjuntos que **ya están guardados**, distintos de los recién elegidos. */
  const [yaSubidos, setYaSubidos] = useState<AdjuntoMensaje[]>([]);
  const [cargando, setCargando] = useState(idExistente !== null);

  useEffect(() => {
    if (!idExistente) return;

    pedir<MensajeGuardado>(`/mensajes/${idExistente}`)
      .then((m) => {
        setTitulo(m.titulo);
        setCuerpo(m.cuerpo);
        setAlcance(m.alcance);
        setParaTodas(m.paraTodas);
        setElegidas(m.unidades);
        setYaSubidos(m.adjuntos);
      })
      .catch((e) => setError(e instanceof ErrorApi ? e.message : 'No se pudo abrir el aviso'))
      .finally(() => setCargando(false));
  }, [idExistente]);

  /** Quita uno de los que ya estaban guardados. Se borra del servidor al vuelo. */
  async function quitarSubido(adjuntoId: string) {
    try {
      await pedir(`/mensajes/${idExistente}/adjuntos/${adjuntoId}`, { metodo: 'DELETE' });
      setYaSubidos((a) => a.filter((x) => x.id !== adjuntoId));
    } catch (e) {
      setError(e instanceof ErrorApi ? e.message : 'No se pudo quitar el archivo');
    }
  }

  const [busca, setBusca] = useState('');
  const buscada = useRetardo(busca);
  const unidades = useRecurso<Pagina<Unidad>>(
    paraTodas ? null : consulta('/padron/unidades', { porPagina: 20, buscar: buscada }),
  );

  function alternar(u: Unidad) {
    setElegidas((e) => (e.some((x) => x.id === u.id) ? e.filter((x) => x.id !== u.id) : [...e, u]));
  }

  /**
   * ## Lo primero es copiar la lista
   *
   * `FileList` es un objeto **vivo** del input: no es una copia de lo que se
   * eligió, es una ventana a lo que el input tiene ahora mismo. Y limpiar el
   * input —que hace falta para poder volver a elegir el mismo archivo— la
   * vacía.
   *
   * Guardándola tal cual en el estado, el `setArchivos` funcional corre
   * **después** de que el manejador termina, con la lista ya vacía. Resultado:
   * se elegía un archivo, no aparecía ninguno, y nada fallaba. Copiarla aquí,
   * en la primera línea, es lo que lo evita.
   */
  function agregarArchivos(lista: FileList | readonly File[] | null) {
    const nuevos = Array.from(lista ?? []);
    if (nuevos.length === 0) return;

    const grande = nuevos.find((f) => f.size > ADJUNTO_MAX_BYTES);
    if (grande) {
      // Se avisa aquí y no al subir: enterarse de que el video no cabe después
      // de esperar la subida es la peor forma de saberlo.
      setError(
        `${grande.name} pesa ${Math.round(grande.size / 1024 / 1024)} MB y el máximo son 2 MB.`,
      );
      return;
    }

    const noAdmitido = nuevos.find((f) => !(ADJUNTO_MIMES as readonly string[]).includes(f.type));
    if (noAdmitido) {
      // El `accept` del input es una sugerencia: en el diálogo del sistema se
      // puede elegir «todos los archivos», y arrastrando no filtra nada.
      setError(`${noAdmitido.name} no es una imagen ni un video de los que se aceptan.`);
      return;
    }

    setError(null);
    setArchivos((a) => [...a, ...nuevos]);
  }

  async function guardar(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setGuardando(true);
    try {
      const datos = {
        titulo,
        cuerpo,
        alcance,
        paraTodas,
        unidades: paraTodas ? [] : elegidas.map((u) => u.id),
      };

      // Corregir no crea otro aviso: se escribe encima del mismo borrador, y
      // los adjuntos que ya tenía se quedan donde estaban.
      const id = idExistente
        ? (await pedir(`/mensajes/${idExistente}`, { metodo: 'PATCH', cuerpo: datos }), idExistente)
        : (await pedir<{ id: string }>('/mensajes', { metodo: 'POST', cuerpo: datos })).id;

      for (const archivo of archivos) {
        await subirArchivo(`/mensajes/${id}/adjuntos`, archivo, archivo.name);
      }

      alGuardar();
    } catch (e) {
      setError(e instanceof ErrorApi ? e.message : 'No se pudo guardar el aviso');
      setGuardando(false);
    }
  }

  return (
    <Modal titulo={idExistente ? 'Corregir el aviso' : 'Nuevo aviso'} abierto alCerrar={alCerrar}>
      {cargando ? (
        <Cargando />
      ) : (
        <form onSubmit={guardar} className="flex flex-col gap-4">
          <Campo etiqueta="Título">
            <Entrada value={titulo} onChange={(e) => setTitulo(e.target.value)} maxLength={120} />
          </Campo>

          <Campo etiqueta="Aviso">
            <textarea
              value={cuerpo}
              onChange={(e) => setCuerpo(e.target.value)}
              rows={5}
              maxLength={4000}
              className="w-full rounded-lg border border-outline bg-surface p-3 text-body-md text-on-surface"
            />
          </Campo>

          <Campo etiqueta="¿A quién de cada vivienda?">
            <Seleccion
              value={alcance}
              onChange={(e) => setAlcance(e.target.value as AlcanceMensaje)}
            >
              {ALCANCES.map((a) => (
                <option key={a.valor} value={a.valor}>
                  {a.texto} — {a.ayuda}
                </option>
              ))}
            </Seleccion>
          </Campo>

          <Campo etiqueta="¿A qué viviendas?">
            <div className="flex flex-col gap-2">
              <label className="flex items-center gap-2 text-body-md text-on-surface">
                <input
                  type="radio"
                  checked={paraTodas}
                  onChange={() => setParaTodas(true)}
                  name="destino"
                />
                Todas las viviendas
              </label>
              <label className="flex items-center gap-2 text-body-md text-on-surface">
                <input
                  type="radio"
                  checked={!paraTodas}
                  onChange={() => setParaTodas(false)}
                  name="destino"
                />
                Elegir viviendas
              </label>
            </div>
          </Campo>

          {!paraTodas && (
            <div className="flex flex-col gap-2">
              <Buscador
                value={busca}
                onChange={(e) => setBusca(e.target.value)}
                placeholder="Busca por clave: A-12, Villa 3…"
              />

              {elegidas.length > 0 && (
                <div className="flex flex-wrap gap-1">
                  {elegidas.map((u) => (
                    <button key={u.id} type="button" onClick={() => alternar(u)}>
                      <Chip tono="ok">{u.clave} ✕</Chip>
                    </button>
                  ))}
                </div>
              )}

              <div className="max-h-48 overflow-y-auto rounded-lg border border-outline-variant">
                {unidades.datos?.datos.map((u) => (
                  <button
                    key={u.id}
                    type="button"
                    onClick={() => alternar(u)}
                    className="flex w-full items-center justify-between px-3 py-2 text-left text-body-md text-on-surface hover:bg-surface-variant"
                  >
                    {u.clave}
                    {elegidas.some((x) => x.id === u.id) && (
                      <span className="text-success">
                        <Iconos.correcto tam={18} />
                      </span>
                    )}
                  </button>
                ))}
              </div>
            </div>
          )}

          <Campo etiqueta="Imágenes o video">
            {/*
            El `<input type="file">` de serie no se puede vestir: el navegador
            pinta su propio «Elegir archivos · Ningún archivo seleccionado», que
            no se parece a nada del portal y ni siquiera se lee como un botón.

            Así que se esconde y se dispara desde una zona propia. Es lo que hace
            todo el mundo, y de paso permite soltar el archivo encima, que en una
            computadora es como se hace normalmente.
          */}
            <input
              ref={entradaArchivos}
              type="file"
              multiple
              accept={ADJUNTO_MIMES.join(',')}
              onChange={(e) => {
                // **Se copia antes de limpiar.** Vaciar el input vacía también su
                // `FileList`, que es el mismo objeto que se acaba de leer.
                const elegidos = Array.from(e.target.files ?? []);
                // Se limpia para que elegir **el mismo** archivo otra vez vuelva a
                // disparar `change`: si no, quitarlo y volver a elegirlo no hace
                // nada y parece que la pantalla se colgó.
                e.target.value = '';
                agregarArchivos(elegidos);
              }}
              className="hidden"
            />

            <button
              type="button"
              onClick={() => entradaArchivos.current?.click()}
              onDragOver={(e) => {
                e.preventDefault();
                setArrastrando(true);
              }}
              onDragLeave={() => setArrastrando(false)}
              onDrop={(e) => {
                e.preventDefault();
                setArrastrando(false);
                agregarArchivos(e.dataTransfer.files);
              }}
              className={`flex w-full flex-col items-center gap-1 rounded-xl border-2 border-dashed px-4 py-6 text-center transition-colors ${
                arrastrando
                  ? 'border-primary bg-primary-container/20'
                  : 'border-outline-variant hover:border-primary hover:bg-surface-container-low'
              }`}
            >
              <span className="text-primary">
                <Iconos.agregar tam={24} />
              </span>
              <span className="text-label-bold text-on-surface">Elegir una imagen o un video</span>
              <span className="text-label-sm text-on-surface-variant">
                o arrástralo aquí · JPG, PNG, WebP, MP4 o WebM · 2 MB como máximo
              </span>
            </button>

            {/* Los que ya estaban guardados van primero y se distinguen: quitarlos
              se hace en el servidor al momento, no al guardar. */}
            {yaSubidos.length > 0 && (
              <ul className="mt-3 flex flex-col gap-2">
                {yaSubidos.map((a) => (
                  <li
                    key={a.id}
                    className="flex items-center justify-between gap-3 rounded-lg bg-surface-container-low px-3 py-2"
                  >
                    <span className="min-w-0 truncate text-body-md text-on-surface">
                      {a.tipo === 'VIDEO' ? 'Video' : 'Imagen'} ya adjuntada
                    </span>
                    <span className="flex shrink-0 items-center gap-3">
                      <span className="text-label-sm text-on-surface-variant">
                        {Math.round(a.bytes / 1024)} KB
                      </span>
                      <button
                        type="button"
                        aria-label="Quitar el archivo"
                        onClick={() => void quitarSubido(a.id)}
                        className="text-outline hover:text-error"
                      >
                        <Iconos.cerrar tam={18} />
                      </button>
                    </span>
                  </li>
                ))}
              </ul>
            )}

            {archivos.length > 0 && (
              <ul className="mt-3 flex flex-col gap-2">
                {archivos.map((a, i) => (
                  <li
                    key={`${a.name}-${i}`}
                    className="flex items-center justify-between gap-3 rounded-lg bg-surface-container-low px-3 py-2"
                  >
                    <span className="min-w-0 truncate text-body-md text-on-surface">{a.name}</span>
                    <span className="flex shrink-0 items-center gap-3">
                      <span className="text-label-sm text-on-surface-variant">
                        {Math.round(a.size / 1024)} KB
                      </span>
                      <button
                        type="button"
                        aria-label={`Quitar ${a.name}`}
                        onClick={() => setArchivos((x) => x.filter((_, j) => j !== i))}
                        className="text-outline hover:text-error"
                      >
                        <Iconos.cerrar tam={18} />
                      </button>
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </Campo>

          {error && <ErrorCaja>{error}</ErrorCaja>}

          {/* Se guarda como borrador. Publicar es otro paso, a propósito: es el
            único que no se puede deshacer. */}
          <div className="flex justify-end gap-2">
            <Boton variante="texto" type="button" onClick={alCerrar}>
              Cancelar
            </Boton>
            <Boton variante="primario" type="submit" disabled={guardando || !titulo || !cuerpo}>
              {guardando ? 'Guardando…' : idExistente ? 'Guardar cambios' : 'Guardar borrador'}
            </Boton>
          </div>
        </form>
      )}
    </Modal>
  );
}
